import { useState } from 'react';
import axios from 'axios';
import { ArrowRight, Mail, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const INITIAL = {
  email: '',
  first_name: '',
  last_name: '',
  profession: '',
  company: '',
  country: '',
  phone: '',
  consent: false,
};

const PROFESSIONS = [
  'Commercial flight attendant',
  'ACMI / charter',
  'VIP',
  'Corporate',
  'No flying experience',
];

const inputClass =
  'w-full bg-transparent border-b border-[#E5E0D8] text-sm py-3 px-0 focus:outline-none focus:border-[#C5A880] transition-colors placeholder-[#9A9A9A]';
const labelClass = 'overline text-[#5A5A5A] block mb-1';

const JobAlertsForm = () => {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');

  const set = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.consent) {
      setError('Please confirm your consent to receive Gate 33 job alerts.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/api/careers/job-alerts/subscribe`, form);
      setSuccess(r.data?.message || 'Check your inbox to confirm.');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div
        data-testid="job-alerts-success"
        className="bg-white border border-[#C5A880] p-10 md:p-14 text-center"
      >
        <CheckCircle2 size={28} className="text-[#C5A880] mx-auto mb-5" strokeWidth={1.5} />
        <h3 className="font-heading text-2xl tracking-tight text-[#0F0F0F] mb-3">
          You&rsquo;re on the list.
        </h3>
        <p className="text-[#5A5A5A] max-w-md mx-auto leading-relaxed">
          {success} We&rsquo;ll be in touch when a new operator role opens.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="job-alerts-form"
      className="bg-white border border-[#E5E0D8] p-8 md:p-12"
      noValidate
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-7">
        {/* Email — full width */}
        <div className="md:col-span-2">
          <label htmlFor="ja-email" className={labelClass}>Email *</label>
          <input
            id="ja-email"
            data-testid="ja-email"
            type="email"
            required
            value={form.email}
            onChange={set('email')}
            placeholder="you@example.com"
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor="ja-fname" className={labelClass}>First name *</label>
          <input
            id="ja-fname"
            data-testid="ja-fname"
            type="text"
            required
            value={form.first_name}
            onChange={set('first_name')}
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="ja-lname" className={labelClass}>Last name *</label>
          <input
            id="ja-lname"
            data-testid="ja-lname"
            type="text"
            required
            value={form.last_name}
            onChange={set('last_name')}
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor="ja-prof" className={labelClass}>Current role *</label>
          <select
            id="ja-prof"
            data-testid="ja-profession"
            required
            value={form.profession}
            onChange={set('profession')}
            className={`${inputClass} appearance-none cursor-pointer`}
          >
            <option value="" disabled>Select…</option>
            {PROFESSIONS.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="ja-company" className={labelClass}>Current company *</label>
          <input
            id="ja-company"
            data-testid="ja-company"
            type="text"
            required
            value={form.company}
            onChange={set('company')}
            placeholder="If unemployed, put Unemployed"
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor="ja-country" className={labelClass}>Country *</label>
          <input
            id="ja-country"
            data-testid="ja-country"
            type="text"
            required
            value={form.country}
            onChange={set('country')}
            placeholder="France, UAE, USA…"
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="ja-phone" className={labelClass}>Phone *</label>
          <input
            id="ja-phone"
            data-testid="ja-phone"
            type="tel"
            required
            value={form.phone}
            onChange={set('phone')}
            placeholder="+33 6 12 34 56 78"
            className={inputClass}
          />
        </div>
      </div>

      {/* Consent — GDPR */}
      <div className="mt-10 pt-7 border-t border-[#E5E0D8]">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            data-testid="ja-consent"
            checked={form.consent}
            onChange={set('consent')}
            className="mt-1 accent-[#C5A880] w-4 h-4 flex-shrink-0"
          />
          <span className="text-xs text-[#5A5A5A] leading-relaxed">
            I agree to receive private aviation job alerts from Gate 33 by email. I can unsubscribe at any time via the link in every email. My data is processed in accordance with Gate 33&rsquo;s privacy policy and the GDPR.
          </span>
        </label>
      </div>

      {error && (
        <p data-testid="ja-error" className="mt-5 text-sm text-[#C0392B]">{error}</p>
      )}

      <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
        <p className="text-xs text-[#9A9A9A] inline-flex items-center gap-2">
          <Mail size={12} /> Roughly 1–2 emails / month. No spam, ever.
        </p>
        <button
          type="submit"
          data-testid="ja-submit"
          disabled={submitting}
          className="inline-flex items-center gap-2 bg-[#0F0F0F] text-[#FDFBF7] px-10 py-4 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#C5A880] hover:text-[#0F0F0F] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {submitting ? 'Subscribing…' : (<>Subscribe <ArrowRight size={14} /></>)}
        </button>
      </div>
    </form>
  );
};

export default JobAlertsForm;
